import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import "./Sidebar.css";
import { SearchIcon } from './GlobalSearch';

export const APPS = [
  { key: 'tools', title: 'Outils', to: '/outils', icon: '🛠️', label: 'Outils' },
  { key: 'quotes', title: 'Devis Henrri', to: '/devis', icon: '📄', label: 'Devis', allowedLogins: ['aymeric', 'aurelien'] },
  { key: 'home', title: 'Accueil', to: '/home', icon: '⌂', label: 'Accueil' },
  { key: 'private-chat', title: 'Chat privé', to: '/chat-prive', icon: '💬', label: 'Chat privé' },
  { key: 'objectives-t4', title: 'Objectifs commerciaux T4 2026', to: '/objectifs-t4', icon: '🎯', label: 'Objectifs T4' },
  { key: 'backup', title: 'Backups', to: '/backup', icon: '📦', label: 'Backups' },
  { key: 'planning', title: 'Planning', to: '/planning', icon: '📅', label: 'Planning' },
  { key: 'appels', title: 'Appels', to: '/appels', icon: '📞', label: 'Appels' },
  { key: 'cdr', title: 'CDR', to: '/cdr', icon: '☕', label: 'CDR' },
  { key: 'suivi', title: 'Suivi Clients', to: '/suivi', icon: '📊', label: 'Suivi Clients' },
  { key: 'stock', title: 'Stock', to: '/stock', icon: '📦', label: 'Stock' },
  { key: 'kanban', title: 'Kanban', to: '/kanban', icon: '🧾', label: 'Kanban' },
  { key: 'aurelien_kanban', title: 'Kanban Aurélien', to: '/kanban_aurelien', icon: '🧾', label: 'Kanban Aurélien', allowedLogins: ['aurelien'] },
  { key: 'commercial', title: 'Pilotage commercial', to: '/commercial', icon: '📈', label: 'Commercial', allowedLogins: ['aurelien'] },
  { key: 'client', title: 'Client', to: '/client', icon: '👥', label: 'Client' },
  { key: 'comcom', title: 'Heures', to: '/comcom', icon: '⏱️', label: 'Heures' },
  { key: 'sms', title: 'SMS', to: '/sms', icon: '✉️', label: 'SMS' },

  // Module natif CRM
  { key: 'users', title: 'Gestion équipe', to: '/users', icon: '👷', label: 'Équipe', masterOnly: true },

  // Visibilité spéciale
  { key: 'aymeric_kanban', title: 'AV Kanban', to: '/kanban_aymeric', icon: '🧾', label: 'AV Kanban', allowedLogins: ['aymeric', 'aurelien'] }
];

const PREFS_URL = 'https://cms.amservices.eu/api/preferences.php';

const Sidebar = ({ user, setUser, theme, onThemeChange, onSearch }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [imgKey, setImgKey] = useState(Date.now());
  const [showSettings, setShowSettings] = useState(false);
  const [visibleApps, setVisibleApps] = useState(APPS.map(app => app.key));

  const navigate = useNavigate();

  const userLogin = user?.login || user?.user || user?.username || '';
  const userLoginLower = String(userLogin).trim().toLowerCase();

  const userName = user?.nom || user?.name || user?.username || 'Utilisateur';

  const isAdmin =
    user?.role === "admin" ||
    localStorage.getItem("isAdmin") === "true" ||
    userLoginLower === "aurelien";

  const isMaster = ["aurelien", "aymeric"].includes(userLoginLower);

  const avatarUrl = userLogin
    ? `https://cms.amservices.eu/assets/avatars/${userLogin}.jpg`
    : '';

  const fallbackUrl =
    `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=007bff&color=fff`;

  useEffect(() => {
    if (!userLogin) return;

    fetch(`${PREFS_URL}?login=${encodeURIComponent(userLogin)}`)
      .then(res => res.json())
      .then(data => {
        if (!data.success || !data.prefs) return;

        if (data.prefs.theme === 'light' || data.prefs.theme === 'dark') {
          onThemeChange(data.prefs.theme);
        }

        if (Array.isArray(data.prefs.visibleApps)) {
          setVisibleApps(data.prefs.visibleApps);
          return;
        }

        const apps = Object.keys(data.prefs)
          .filter(key => data.prefs[key] === true);

        if (apps.length) {
          setVisibleApps(apps);
        }
      })
      .catch(err => console.error("Erreur chargement préférences", err));
  }, [userLogin, onThemeChange]);

  const savePreferences = async (newVisibleApps) => {
    if (!userLogin) return;

    try {
      await fetch(PREFS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          login: userLogin,
          prefs: {
            visibleApps: newVisibleApps
          }
        })
      });
    } catch (err) {
      console.error("Erreur sauvegarde préférences", err);
    }
  };

  const canAccessApp = (app) => {
    if (app.key === 'private-chat' && userLoginLower === 'tableau') {
      return false;
    }
    if (app.masterOnly && !isMaster) {
      return false;
    }

    if (app.adminOnly && !isAdmin) {
      return false;
    }

    if (Array.isArray(app.allowedLogins) && app.allowedLogins.length > 0) {
      return app.allowedLogins
        .map(login => String(login).toLowerCase())
        .includes(userLoginLower);
    }

    return true;
  };

  const isForcedVisible = (app) => {
    if (app.key === "home" || app.key === "tools" || app.key === "private-chat" || app.key === "objectives-t4" || (app.key === "quotes" && isMaster)) {
      return true;
    }

    if (app.key === "users" && isMaster) {
      return true;
    }

    if (
      app.key === "aurelien_kanban" &&
      userLoginLower === "aurelien"
    ) {
      return true;
    }

    if (app.key === "commercial" && userLoginLower === "aurelien") {
      return true;
    }

    if (
      app.key === "aymeric_kanban" &&
      ["aymeric", "aurelien"].includes(userLoginLower)
    ) {
      return true;
    }

    return false;
  };

  const shouldDisplayApp = (app) => {
    if (!canAccessApp(app)) {
      return false;
    }

    if (isForcedVisible(app)) {
      return true;
    }

    return visibleApps.includes(app.key);
  };

  const toggleAppVisibility = (appKey) => {
    let newVisibleApps;

    if (visibleApps.includes(appKey)) {
      newVisibleApps = visibleApps.filter(key => key !== appKey);
    } else {
      newVisibleApps = [...visibleApps, appKey];
    }

    setVisibleApps(newVisibleApps);
    savePreferences(newVisibleApps);
  };

  const handleThemeToggle = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    onThemeChange(nextTheme);

    if (userLogin) {
      fetch(PREFS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: userLogin, prefs: { theme: nextTheme } })
      }).catch(err => console.error("Erreur sauvegarde du thème", err));
    }
  };

  const handleImageUpload = async (e) => {
    if (!userLogin) return;

    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('login', userLogin);

    try {
      const response = await fetch(
        'https://cms.amservices.eu/api/upload_avatar.php',
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();

      if (data.success) {
        setImgKey(Date.now());
        alert("Photo mise à jour !");
      } else {
        alert(data.message || "Erreur lors de l'envoi de la photo");
      }
    } catch (err) {
      console.error("Erreur upload", err);
      alert("Erreur lors de l'envoi de la photo");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('am_user');
    localStorage.removeItem('am_token');
    localStorage.removeItem('ams_token');
    localStorage.removeItem('adce_token');
    localStorage.removeItem('ams_user');
    localStorage.removeItem('adce_user');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userRole');

    setUser(null);
    navigate('/login');
  };

  return (
    <>
      <aside className={`stat-card sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>

        <button
          className="toggle-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? '➡' : '⬅'}
        </button>

        <div className="sidebar-header">

          {!isCollapsed && userLogin && (
            <div className="profile-picture-container">

              <img
                key={imgKey}
                src={`${avatarUrl}?t=${imgKey}`}
                alt="Profil"
                className="profile-img"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = fallbackUrl;
                }}
              />

              <label className="avatar-upload-btn">
                📷
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  hidden
                />
              </label>

            </div>
          )}

          <h2 className="ok-text">
            {isCollapsed ? 'AMS' : 'AM SERVICES'}
          </h2>

          {!isCollapsed && (
            <div className="user-line">
              <p className="stat-label">
                {userName}
              </p>
              <div className="user-tools">
                <button
                  className="board-shortcut-btn"
                  onClick={() => navigate('/notes-board')}
                  title="Ouvrir le mode tableau"
                  aria-label="Ouvrir le mode tableau"
                >
                  ▦
                </button>
                <button
                  className="settings-icon-btn"
                  onClick={() => setShowSettings(true)}
                  title="Applications visibles"
                  aria-label="Applications visibles"
                >
                  ⚙️
                </button>
              </div>
            </div>
          )}

        </div>

        <nav className="sidebar-nav">
          {onSearch && <button className="global-search-trigger" onClick={onSearch} title="Recherche globale (Ctrl+K)" aria-label="Ouvrir la recherche globale" aria-keyshortcuts="Control+k Meta+k"><SearchIcon />{!isCollapsed && <><span>Rechercher</span><kbd>Ctrl K</kbd></>}</button>}
          {APPS
            .filter(shouldDisplayApp)
            .map(app => (
              <NavLink
                key={app.key}
                title={app.title}
                to={app.to}
                className="btn nav-btn"
              >
                <span>{app.icon}</span> {!isCollapsed && app.label}
              </NavLink>
            ))}
        </nav>

        {!isCollapsed && (
          <button
            type="button"
            className="btn theme-toggle-btn"
            onClick={handleThemeToggle}
            aria-label={theme === 'light' ? 'Activer le mode sombre' : 'Activer le mode clair'}
            title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}
          >
            <span aria-hidden="true">{theme === 'light' ? '☾' : '☀'}</span>
            <span>{theme === 'light' ? 'Mode sombre' : 'Mode clair'}</span>
          </button>
        )}

        <button
          onClick={handleLogout}
          className="btn logout-btn"
        >
          <span>🚪</span> {!isCollapsed && "Déconnexion"}
        </button>

      </aside>

      {showSettings && (
        <div className="apps-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="apps-modal" onClick={(e) => e.stopPropagation()}>

            <div className="apps-modal-header">
              <h3>Applications visibles</h3>

              <button
                className="apps-modal-close"
                onClick={() => setShowSettings(false)}
              >
                ✕
              </button>
            </div>

            <div className="apps-modal-content">
              {APPS
                .filter(canAccessApp)
                .map(app => (
                  <label key={app.key} className="app-checkbox-line">
                    <input
                      type="checkbox"
                      checked={visibleApps.includes(app.key) || isForcedVisible(app)}
                      onChange={() => toggleAppVisibility(app.key)}
                      disabled={isForcedVisible(app)}
                    />
                    <span>{app.icon}</span>
                    <span>{app.label}</span>
                  </label>
                ))}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
